<script setup lang="ts">
  import { ref, watch } from 'vue'
  import { User, Building2, Landmark, Info } from 'lucide-vue-next'
  import { EntityTypeEnum } from '@/types/generated/types.gen'
  import { useTranslation } from '@/composables/useTranslation'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import type { CustomerCase, AdvowareRoleMapping } from '@/types/generated/types.gen'
  import { AdvowareRolesService } from '@/types/generated/services.gen'
  import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
  import { useDebounceFn } from '@vueuse/core'

  const props = defineProps<{
    caseData: CustomerCase | null
    selectedEntitiesRelationships: Array<string>
  }>()

  const { t } = useTranslation({
    en: {
      exportPreviewTitle: 'Export Preview for Advoware',
      exportPreviewDescription:
        'Advoware supports only one role per participant. This preview shows the actual export result.',
      loadingRoleMappings: 'Loading role mappings...',
      errorLoadingMappings: 'Error loading role assignments.',
      roleMismatchTooltip:
        'The role "{jupusRole}" is not available in Advoware and will therefore be exported as "Sonstiger".',
      roleCorrectTooltip:
        'Advoware only supports export with a single role. The role "{role}" will be exported.',
      unnamed: 'Unnamed'
    },
    de: {
      exportPreviewTitle: 'Exportvorschau für Advoware',
      exportPreviewDescription:
        'Advoware unterstützt pro Beteiligtem nur eine Rolle. Diese Vorschau zeigt das tatsächliche Export-Ergebnis.',
      loadingRoleMappings: 'Rollemappingen werden geladen...',
      errorLoadingMappings: 'Fehler beim Laden der Rollen Zuordnungen.',
      roleMismatchTooltip:
        'Die Rolle "{jupusRole}" ist in Advoware nicht vorhanden und wird daher als "Sonstiger" exportiert.',
      roleCorrectTooltip:
        'Advoware unterstützt nur den Export mit einer einzigen Rolle. Die Rolle „{role}" wird exportiert.',
      unnamed: 'Unnamed'
    }
  })

  const caseStore = useCaseStore()
  const roleMappings = ref<AdvowareRoleMapping[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const getEntityIcon = (relationshipUuid: string) => {
    const entityType = getEntityType(relationshipUuid)
    switch (entityType) {
      case EntityTypeEnum.NATURAL_PERSON:
        return User
      case EntityTypeEnum.LEGAL_PERSON:
        return Building2
      case EntityTypeEnum.INSTITUTION:
        return Landmark
      default:
        return User
    }
  }

  const getEntityName = (relationshipUuid: string): string => {
    if (!caseStore.caseRecord?.data?.entities) return ''

    const entityWithRelationship = caseStore.caseRecord.data.entities.find(
      (e) => e.uuid === relationshipUuid
    )
    if (!entityWithRelationship?.entity) return ''

    const e = entityWithRelationship.entity
    if (e.entity_type === EntityTypeEnum.NATURAL_PERSON) {
      const parts = [e.title, e.first_name, e.last_name].filter(Boolean)
      return parts.join(' ') || t('unnamed')
    }
    if (e.entity_type === EntityTypeEnum.LEGAL_PERSON) {
      return e.company_name || t('unnamed')
    }
    if (e.entity_type === EntityTypeEnum.INSTITUTION) {
      return e.institution_name || t('unnamed')
    }
    return t('unnamed')
  }

  const getEntityType = (relationshipUuid: string): EntityTypeEnum | null => {
    if (!caseStore.caseRecord?.data?.entities) return null

    const entityWithRelationship = caseStore.caseRecord.data.entities.find(
      (e) => e.uuid === relationshipUuid
    )
    return entityWithRelationship?.entity?.entity_type || null
  }

  const getTooltipText = (mapping: { jupus_role: string; advoware_role: string }) => {
    const isSonstigeCase =
      (mapping.jupus_role === 'Sonstige' || mapping.jupus_role === 'Sonstiger') &&
      mapping.advoware_role === 'Sonstiger'
    const isMatch = mapping.jupus_role === mapping.advoware_role || isSonstigeCase

    if (isMatch) {
      return t('roleCorrectTooltip', { role: mapping.advoware_role })
    }
    return t('roleMismatchTooltip', { jupusRole: mapping.jupus_role })
  }

  const fetchRoleMappings = async () => {
    if (!props.caseData?.uuid || props.selectedEntitiesRelationships.length === 0) {
      roleMappings.value = []
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await AdvowareRolesService.advowareRolesGetRoleMappingsCreate({
        requestBody: {
          case_uuid: props.caseData.uuid,
          entity_uuids: props.selectedEntitiesRelationships
        }
      })

      const mappings = response as unknown as Array<{
        entity_uuid: string
        jupus_role: string
        advoware_role: string
      }>
      roleMappings.value = mappings
    } catch (err) {
      error.value = t('errorLoadingMappings')
      console.error('Error fetching role mappings:', err)
      roleMappings.value = []
    } finally {
      isLoading.value = false
    }
  }

  const debouncedFetchRoleMappings = useDebounceFn(fetchRoleMappings, 100)

  watch(
    () => [props.caseData?.uuid, props.selectedEntitiesRelationships],
    () => {
      if (props.caseData?.uuid && props.selectedEntitiesRelationships.length > 0) {
        debouncedFetchRoleMappings()
      } else {
        roleMappings.value = []
      }
    },
    { immediate: true, deep: true }
  )

  // Watch for changes in entity relationships to refresh when roles change
  watch(
    () => caseStore.caseRecord?.data?.entities,
    () => {
      if (props.caseData?.uuid && props.selectedEntitiesRelationships.length > 0) {
        debouncedFetchRoleMappings()
      }
    },
    { deep: true }
  )
</script>

<template>
  <div
    v-if="selectedEntitiesRelationships.length > 0"
    class="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm"
  >
    <div class="mb-4 flex flex-col gap-2">
      <h3 class="text-lg font-semibold text-gray-900">{{ t('exportPreviewTitle') }}</h3>
      <p class="text-base text-gray-600">{{ t('exportPreviewDescription') }}</p>
    </div>

    <div
      v-if="isLoading"
      class="py-4 text-center text-sm text-gray-500"
    >
      {{ t('loadingRoleMappings') }}
    </div>

    <div
      v-else-if="error"
      class="py-4 text-center text-sm text-red-600"
    >
      {{ error }}
    </div>

    <div
      v-else-if="roleMappings.length > 0"
      class="flex flex-wrap gap-3"
    >
      <div
        v-for="mapping in roleMappings"
        :key="mapping.entity_uuid"
        class="flex min-w-[200px] flex-shrink-0 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3
          py-1.5"
      >
        <component
          :is="getEntityIcon(mapping.entity_uuid)"
          class="h-5 w-5 flex-shrink-0 text-gray-600"
        />
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-semibold text-gray-900">
            {{ getEntityName(mapping.entity_uuid) }}
          </div>
          <div class="text-xs text-gray-500">{{ mapping.advoware_role }}</div>
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger as-child>
              <Info class="h-4 w-4 flex-shrink-0 text-gray-500" />
            </TooltipTrigger>
            <TooltipContent
              variant="secondary"
              :show-arrow="true"
              class="max-w-xs"
            >
              {{ getTooltipText(mapping) }}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  </div>
</template>
